import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { CreateBankTransactionModel, BankMutation } from '../../../types/bankAccount';
import Button from '../buttons/Button';
import { formatEuroCents } from '../../../utils/bankAccountUtil';
import * as bankAccountService from '../../../services/bankAccountService';

type InternalMutationMatch = {
  mutation: CreateBankTransactionModel;
  counterpartMutation: BankMutation;
  confirmed: boolean;
};

type VerifyInternalMutationMatchesProps = {
  unassignedMutations: BankMutation[];
  onConfirm: (confirmedMatches: InternalMutationMatch[]) => void;
};

export default function VerifyInternalMutationMatchesView({
  unassignedMutations,
  onConfirm,
}: VerifyInternalMutationMatchesProps) {
  const { t, i18n } = useTranslation('', { keyPrefix: 'component.modal.uploadFinancialFile' });
  const [matches, setMatches] = useState<InternalMutationMatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const findInternalMatches = async () => {
      try {
        setLoading(true);
        const usedCounterpartIdsLocal = new Set<number>();

        // Process mutations sequentially to avoid duplicate counterpart matches
        const internalMatches = await unassignedMutations.reduce(async (accPromise, mutation) => {
          const acc = await accPromise;
          
          // Skip if this mutation's counterpart is already used
          if (usedCounterpartIdsLocal.has(mutation.mutationId)) {
            return acc;
          }

          try {
            // Get potential internal transfer candidates for this mutation
            const candidates = await bankAccountService.getInternalTransferCandidates(
              mutation.mutationId,
              { withinDays: 7, strictAmount: true, limit: 5 }
            );

            // Find the first candidate that hasn't been used yet
            const availableCandidate = candidates.find(
              (candidate) => !usedCounterpartIdsLocal.has(candidate.mutationId)
            );

            if (availableCandidate) {
              // Create a transaction model from the mutation
              const transaction: CreateBankTransactionModel = {
                mutationId: mutation.mutationId,
                amount: mutation.amount,
                date: mutation.transactionDate,
                description: mutation.description,
                transactionType: 'INTERNAL_BANK_TRANSFER' as any, // Will be set properly when creating
              };

              acc.push({
                mutation: transaction,
                counterpartMutation: availableCandidate,
                confirmed: false,
              });

              // Mark this counterpart as used locally
              usedCounterpartIdsLocal.add(availableCandidate.mutationId);
            }
          } catch (error) {
            console.error(`Error finding candidates for mutation ${mutation.mutationId}:`, error);
            // Continue with other mutations even if one fails
          }

          return acc;
        }, Promise.resolve([] as InternalMutationMatch[]));

        setMatches(internalMatches);
        
        // If no matches found, automatically proceed to next step after a short delay
        if (internalMatches.length === 0) {
          setTimeout(() => {
            onConfirm([]);
          }, 1500);
        }
      } catch (error) {
        console.error('Error finding internal mutation matches:', error);
        setMatches([]);
        // Even on error, proceed to next step
        setTimeout(() => {
          onConfirm([]);
        }, 1500);
      } finally {
        setLoading(false);
      }
    };

    if (unassignedMutations.length > 0) {
      findInternalMatches();
    } else {
      setLoading(false);
      // No mutations to process, proceed immediately
      setTimeout(() => {
        onConfirm([]);
      }, 500);
    }
  }, [unassignedMutations, onConfirm]);

  const toggleConfirmation = (index: number) => {
    setMatches((prev) =>
      prev.map((match, i) => 
        i === index ? { ...match, confirmed: !match.confirmed } : match
      )
    );
  };

  const handleConfirm = () => {
    const confirmedMatches = matches.filter((match) => match.confirmed);
    onConfirm(confirmedMatches);
  };

  const toggleAll = () => {
    const allConfirmed = matches.every((match) => match.confirmed);
    setMatches((prev) =>
      prev.map((match) => ({ ...match, confirmed: !allConfirmed }))
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-primary-600">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div>
      {matches.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-primary-600">{t('noInternalMatches')}</p>
          <p className="text-sm text-primary-500 mt-2">
            {t('noInternalMatchesSubtitle')}
          </p>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <p className="text-sm text-primary-600 mb-4">
              {t('internalMatchesFound', { count: matches.length })}
            </p>
          </div>
          
          <ul className="mb-4 space-y-4">
            {matches.map((match, index) => (
              <li
                key={`${index}_internal_${match.mutation.mutationId}`}
                className="rounded-lg border bg-white p-4 shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <input
                    type="checkbox"
                    checked={match.confirmed}
                    onChange={() => toggleConfirmation(index)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Current mutation */}
                      <div className="space-y-2">
                        <h4 className="font-medium text-primary-900">
                          {t('currentMutation')}
                        </h4>
                        <div className="text-sm">
                          <div className="font-medium">{match.mutation.description}</div>
                          <div className="text-primary-600">
                            {formatEuroCents(match.mutation.amount, i18n.language)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(match.mutation.date).toLocaleDateString(i18n.language)}
                          </div>
                        </div>
                      </div>

                      {/* Counterpart mutation */}
                      <div className="space-y-2">
                        <h4 className="font-medium text-primary-900">
                          {t('counterpartMutation')}
                        </h4>
                        <div className="text-sm">
                          <div className="font-medium">{match.counterpartMutation.description}</div>
                          <div className="text-primary-600">
                            {formatEuroCents(match.counterpartMutation.amount, i18n.language)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(match.counterpartMutation.transactionDate).toLocaleDateString(i18n.language)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {t('oppositeAccount')}: {match.counterpartMutation.oppositeAccount}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex justify-between items-center">
            <Button 
              variant="secondary" 
              type="button" 
              onClick={toggleAll}
              disabled={matches.length === 0}
            >
              {matches.every((match) => match.confirmed) ? t('deselectAll') : t('selectAll')}
            </Button>
            
            <Button 
              variant="primary-dark" 
              type="button" 
              onClick={handleConfirm}
            >
              {t('continue')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
